
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '../services/auth.service';
import { queryKeys } from '../lib/react-query';
import { notifyUser, logoutUser } from '../util/util';
import secureLocalStorage from 'react-secure-storage';
import { extractErrorMessage } from './extractErrorMessage';

// Auth queries
export const useProfile = () => {
  return useQuery({
    queryKey: queryKeys.auth.user,
    queryFn: async () => {
      // Normal backend call
      return authApi.getProfile({ method: 'GET' }).then(res => res.data);
    },
    enabled: !!secureLocalStorage.getItem('token'),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: 'always',
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
};

// Auth mutations
export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authApi.login,
    onSuccess: async (response) => {
      if (response.data?.success) {
        const { 
          token, name, member_number, phone, email, employer_number, ssnit_number, avatar, dob, date_joined, sex, nationality, notifications_enabled, terms, last_logged_in, last_logged_in_ip, last_logged_in_agent, phone_verified, affluent
        } = response.data.data;
        
        // Phone verification check is handled in Login.jsx component
        // Only store data and show success toast if phone is verified
        if (phone_verified !== "false" && phone_verified !== false) {
          const user = { name, member_number, phone, email, employer_number, ssnit_number, avatar, dob, date_joined, sex, nationality, notifications_enabled, terms, last_logged_in, last_logged_in_ip, last_logged_in_agent, affluent };
          secureLocalStorage.setItem('token', token);
          secureLocalStorage.setItem('user', JSON.stringify(user));
          secureLocalStorage.setItem('affluent', affluent);
          // Cache the profile in the same shape returned by the profile API
          queryClient.setQueryData(queryKeys.auth.user, { success: true, data: user });
          // Immediately refetch to ensure server-supplied fields are populated (e.g., Ghana Card)
          queryClient.refetchQueries({ queryKey: queryKeys.auth.user, exact: true });
          notifyUser('success', response.data.message || 'Login successful', 'Welcome');
        } else {
          // Phone not verified - show info toast (redirect handled in Login.jsx)
          notifyUser('info', response.data.message || 'Please verify your phone number to continue.', 'Phone Verification');
        }
      } else {
        // Check for invalid credentials / unauthorized
        const responseMessage = response.data?.message || '';
        const isUnauthorized = 
          responseMessage.toLowerCase().includes('unauthorised') ||
          responseMessage.toLowerCase().includes('unauthorized') ||
          responseMessage.toLowerCase().includes('invalid credentials') ||
          response.data?.data?.error === 'Invalid credentials';
        
        if (isUnauthorized) {
          // Map to standardized error format
          const standardizedError = {
            success: false,
            message: 'Unauthorised.',
            data: {
              error: 'Invalid credentials'
            }
          };
          // Update response data for consistent error handling
          response.data = standardizedError;
          notifyUser('error', 'Invalid credentials', 'Login Error');
          throw new Error('Invalid credentials');
        }

        // Phone-not-verified is a success response (HTTP 200, phone_verified: "false") — handled in Login.jsx
        throw new Error(response.data?.message || 'Login failed');
      }
    },
    onError: (error) => {
      // Map error response to standardized format
      const errorData = error?.response?.data;
      const isUnauthorized = error?.response?.status === 401 || 
        errorData?.message?.toLowerCase().includes('unauthorised') ||
        errorData?.message?.toLowerCase().includes('unauthorized') ||
        errorData?.message?.toLowerCase().includes('invalid credentials');
      
      if (isUnauthorized) {
        // Standardize to the expected format
        const standardizedError = {
          success: false,
          message: 'Unauthorised.',
          data: {
            error: 'Invalid credentials'
          }
        };
        
        // Update the error response data for consistent handling
        if (error.response) {
          error.response.data = standardizedError;
        }
        
        notifyUser('error', 'Invalid credentials', 'Login Error');
      } else {
        notifyUser('error', extractErrorMessage(error, 'Login failed'), 'Login Error');
      }
    },
  });
};

export const useVerifyEmail = () => {
  return useMutation({
    mutationFn: authApi.verifyEmail,
    onSuccess: (response) => {
      if (response.data?.success) {
        notifyUser('success', 'Email verified successfully', 'Success');
      } else {
        throw new Error(response.data?.message || 'Email verification failed');
      }
    },
    onError: (error) => {
      notifyUser('error', extractErrorMessage(error, 'Email verification failed'), 'Verification Error');
    },
  });
};

export const useVerifyOtp = () => {
  return useMutation({
    mutationFn: authApi.verifyOtp,
    onSuccess: (response) => {
      if (response.data?.success) {
        notifyUser('success', 'OTP verified successfully', 'Success');
      } else {
        throw new Error(response.data?.message || 'OTP verification failed');
      }
    },
    onError: (error) => {
      notifyUser('error', extractErrorMessage(error, 'OTP verification failed'), 'Verification Error');
    },
  });
};

export const useVerifyResetOtp = () => {
  return useMutation({
    mutationFn: authApi.verifyResetOtp,
    onSuccess: (response) => {
      if (response.data?.success) {
        notifyUser('success', 'Reset OTP verified successfully', 'Success');
      } else {
        throw new Error(response.data?.message || 'Reset OTP verification failed');
      }
    },
    onError: (error) => {
      notifyUser('error', extractErrorMessage(error, 'Reset OTP verification failed'), 'Verification Error');
    },
  });
};

export const useDeleteAccount = () => {
  return useMutation({
    mutationFn: authApi.deleteAccount,
    onSuccess: (response) => {
      if (response.data?.success) {
        notifyUser('success', response.data.message || 'Account deleted successfully', 'Success');
      } else {
        throw new Error(response.data?.message || 'Account deletion failed');
      }
    },
    onError: (error) => {
      notifyUser('error', extractErrorMessage(error, 'Account deletion failed'), 'Error');
    },
  });
};

export const useCreatePassword = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: authApi.createPassword,
    onSuccess: (response) => {
      if (response.data?.success) {
        const { user, accessToken, refreshToken, expiresAt } = response.data.data;
        
        // Store tokens and user data
        secureLocalStorage.setItem('token', accessToken);
        secureLocalStorage.setItem('refreshToken', refreshToken);
        secureLocalStorage.setItem('user', JSON.stringify(user));
        secureLocalStorage.setItem('expiresAt', expiresAt);
        
        // Set user data in cache (use same API response shape)
        queryClient.setQueryData(queryKeys.auth.user, { success: true, data: user });
        queryClient.refetchQueries({ queryKey: queryKeys.auth.user, exact: true });
        
        notifyUser('success', 'Password created successfully', 'Success');
      } else {
        throw new Error(response.data?.message || 'Password creation failed');
      }
    },
    onError: (error) => {
      notifyUser('error', extractErrorMessage(error, 'Password creation failed'), 'Error');
    },
  });
};

export const useForgotPassword = () => {
  return useMutation({
    mutationFn: authApi.forgotPassword,
    // onSuccess and onError handled in components for flexibility
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: ({ data, token }) => authApi.resetPassword(data, token),
    onSuccess: (response) => {
      if (response.data?.success) {
        notifyUser('success', 'Password reset successful. You can now login with your new password.', 'Success');
      } else {
        throw new Error(response.data?.message || 'Password reset failed');
      }
    },
    onError: (error) => {
      notifyUser('error', extractErrorMessage(error, 'Password reset failed'), 'Error');
    },
  });
};

export const useResendVerification = () => {
  return useMutation({
    mutationFn: authApi.resendVerification,
    onSuccess: (response) => {
      if (response.data?.success) {
        notifyUser('success', 'Verification email sent successfully', 'Success');
      } else {
        throw new Error(response.data?.message || 'Failed to resend verification email');
      }
    },
    onError: (error) => {
      notifyUser('error', extractErrorMessage(error, 'Failed to resend verification email'), 'Error');
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      // Clear all cached data
      queryClient.clear();
      
      // Clear local storage
      secureLocalStorage.removeItem('token');
      secureLocalStorage.removeItem('refreshToken');
      secureLocalStorage.removeItem('user');
      secureLocalStorage.removeItem('expiresAt');
      
      notifyUser('success', 'Logged out successfully', 'Success');
    },
    onError: (error) => {
      // Even if the API call fails, we should still log out locally
      queryClient.clear();
      logoutUser();
      
      notifyUser('error', extractErrorMessage(error, 'Logout failed'), 'Error');
    },
  });
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: authApi.updateProfile,
    onSuccess: (response) => {
      if (response.data?.success) {
        // Update cached user data (use full response shape)
        queryClient.setQueryData(queryKeys.auth.user, response.data);
        
        // Update local storage
        secureLocalStorage.setItem('user', JSON.stringify(response.data.data));
        
        notifyUser('success', 'Profile updated successfully', 'Success');
      } else {
        throw new Error(response.data?.message || 'Profile update failed');
      }
    },
    onError: (error) => {
      notifyUser('error', extractErrorMessage(error, 'Profile update failed'), 'Error');
    },
  });
};

export const useChangePassword = () => {
  return useMutation({
    mutationFn: authApi.changePassword,
    onSuccess: (response) => {
      if (response.data?.success) {
        notifyUser('success', 'Password changed successfully', 'Success');
      } else {
        throw new Error(response.data?.message || 'Password change failed');
      }
    },
    onError: (error) => {
      notifyUser('error', extractErrorMessage(error, 'Password change failed'), 'Error');
    },
  });
};

// Referral code query
export const useReferralCode = () => {
  return useQuery({
    queryKey: queryKeys.auth.referralCode,
    queryFn: async () => {
      const response = await authApi.getReferralCode();
      return response.data;
    },
    enabled: !!secureLocalStorage.getItem('token'),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Referred users (referral history) query
export const useReferredUsers = () => {
  return useQuery({
    queryKey: queryKeys.auth.referredUsers,
    queryFn: async () => {
      const response = await authApi.getReferredUsers();
      return response.data;
    },
    enabled: !!secureLocalStorage.getItem('token'),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Create account mutation
export const useCreateAccount = () => {
  return useMutation({
    mutationFn: authApi.createAccount,
    onSuccess: (response) => {
      if (response.data?.success) {
        notifyUser('success', response.data.message || 'Account created successfully', 'Success');
      } else {
        throw new Error(response.data?.message || 'Account creation failed');
      }
    },
    onError: (error) => {
      notifyUser('error', extractErrorMessage(error, 'Account creation failed'), 'Error');
    },
  });
};
